/**
 * Tooltip component — Brand Book §2.7
 * Dark inverted bg, shadow-lg, 8px/12px padding
 * 11px font, 240px max-width, 300ms show delay
 * Arrow via CSS border trick, 4 positions
 */

import { useState, useRef, useEffect, type ReactNode } from 'react';
import { clsx } from 'clsx';

export interface TooltipProps {
  content: string;
  children: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

const TOOLTIP_BG = '#1C1B18';

export function Tooltip({
  content,
  children,
  position = 'top',
  className,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const showRef = useRef<ReturnType<typeof setTimeout>>();
  const hideRef = useRef<ReturnType<typeof setTimeout>>();

  const showTooltip = () => {
    if (hideRef.current) {clearTimeout(hideRef.current);}
    showRef.current = setTimeout(() => setIsVisible(true), 300);
  };

  const hideTooltip = () => {
    if (showRef.current) {clearTimeout(showRef.current);}
    hideRef.current = setTimeout(() => setIsVisible(false), 100);
  };

  useEffect(() => {
    return () => {
      if (showRef.current) {clearTimeout(showRef.current);}
      if (hideRef.current) {clearTimeout(hideRef.current);}
    };
  }, []);

  const positionStyles = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  const arrowStyles = {
    top: `top-full left-1/2 -translate-x-1/2 border-t-[${TOOLTIP_BG}] border-x-transparent border-b-transparent`,
    bottom: `bottom-full left-1/2 -translate-x-1/2 border-b-[${TOOLTIP_BG}] border-x-transparent border-t-transparent`,
    left: `left-full top-1/2 -translate-y-1/2 border-l-[${TOOLTIP_BG}] border-y-transparent border-r-transparent`,
    right: `right-full top-1/2 -translate-y-1/2 border-r-[${TOOLTIP_BG}] border-y-transparent border-l-transparent`,
  };

  return (
    <div
      className={clsx('relative inline-flex', className)}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}

      {isVisible && (
        <div
          className={clsx(
            'absolute z-50 pointer-events-none',
            positionStyles[position]
          )}
          style={{ animation: 'tooltipFadeIn 150ms ease' }}
          role="tooltip"
        >
          <div
            className="shadow-lg w-max max-w-[280px] whitespace-pre-line"
            style={{
              backgroundColor: TOOLTIP_BG,
              padding: '8px 12px',
              fontSize: '11px',
              lineHeight: 1.5,
              color: '#CCC',
            }}
          >
            {content}
          </div>
          {/* Arrow */}
          <div
            className={clsx(
              'absolute w-0 h-0 border-[6px]',
              arrowStyles[position]
            )}
          />
        </div>
      )}
    </div>
  );
}